/**
 * Engine: read-side traversal.
 *
 * Pure lookups over the tree: find-by-key, parent-with-siblings, depth
 * (root = 1, 0 when missing), descendant count, record-id collection, and the
 * root-to-node branch chain consumed by workflow triggers. None of these
 * mutate input. `findNode` returns the first node matching `key`; new nodes
 * are minted with `crypto.randomUUID()` so collisions are not produced going
 * forward, but legacy persisted trees may still carry `${Date.now()}-…` keys
 * until the one-time `migrateRelationshipTreeNodes` script rewrites them.
 */
import type { RelationshipTreeNode } from '../System.RelationshipTree.types';

export const findNode = (
  trees: RelationshipTreeNode[],
  key: string,
): RelationshipTreeNode | undefined => {
  for (const node of trees) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNode(node.children, key);
      if (found) return found;
    }
  }
  return undefined;
};

export const findParentInfo = (
  trees: RelationshipTreeNode[],
  childKey: string,
):
  | { parent: RelationshipTreeNode | null; siblings: RelationshipTreeNode[] }
  | undefined => {
  for (const node of trees) {
    if (node.children) {
      const childIndex = node.children.findIndex((child) => child.key === childKey);
      if (childIndex !== -1) {
        return { parent: node, siblings: node.children };
      }
      const result = findParentInfo(node.children, childKey);
      if (result) return result;
    }
  }
  if (trees.find((node) => node.key === childKey)) {
    return { parent: null, siblings: trees };
  }
  return undefined;
};

export const getNodeDepth = (
  trees: RelationshipTreeNode[],
  key: string,
  currentDepth = 1,
): number => {
  for (const node of trees) {
    if (node.key === key) return currentDepth;
    if (node.children) {
      const depth = getNodeDepth(node.children, key, currentDepth + 1);
      if (depth > 0) return depth;
    }
  }
  return 0;
};

export const countDescendants = (node: RelationshipTreeNode): number => {
  if (!node.children || node.children.length === 0) return 0;
  return node.children.reduce(
    (count, child) => count + 1 + countDescendants(child),
    0,
  );
};

export const getNodeBranch = (
  trees: RelationshipTreeNode[],
  key: string,
): RelationshipTreeNode[] => {
  const walk = (
    nodes: RelationshipTreeNode[],
    ancestors: RelationshipTreeNode[],
  ): RelationshipTreeNode[] | undefined => {
    for (const node of nodes) {
      const branch = [...ancestors, node];
      if (node.key === key) return branch;
      if (node.children) {
        const found = walk(node.children, branch);
        if (found) return found;
      }
    }
    return undefined;
  };
  return walk(trees, []) ?? [];
};

export const collectRecordIds = (trees: RelationshipTreeNode[]): string[] => {
  const ids = new Set<string>();
  const walk = (nodes: RelationshipTreeNode[]): void => {
    for (const n of nodes) {
      if (typeof n.entityRecordId === 'string' && n.entityRecordId.length > 0) {
        ids.add(n.entityRecordId);
      }
      if (n.children) walk(n.children);
    }
  };
  walk(trees);
  return Array.from(ids);
};
